/**
 * Per-user persistence of the chosen affiliate (tenant).
 *
 * The choice lives in `sessionStorage` so it survives a page reload but not a
 * new browser session, matching admin v1. The key is namespaced per app *and*
 * per user: admin v1 runs on the same origin and must not fight over the same
 * slot, and a shared machine must not leak one user's tenant choice to the next.
 *
 * Every access is guarded because `sessionStorage` throws in private-mode and
 * sandboxed contexts; a storage failure degrades to "no stored choice" rather
 * than breaking sign-in.
 */

const STORAGE_KEY_PREFIX = "admin-v2.affiliate:";

function storageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

/** The affiliate id this user last chose, or `null` when there is none. */
export function readStoredAffiliateId(userId: string | null): string | null {
  if (userId === null) {
    return null;
  }
  try {
    const stored = window.sessionStorage.getItem(storageKey(userId));
    if (stored === null) {
      return null;
    }
    const trimmed = stored.trim();
    return trimmed === "" ? null : trimmed;
  } catch {
    return null;
  }
}

/** Persists this user's affiliate choice. No-ops when storage is unavailable. */
export function writeStoredAffiliateId(
  userId: string | null,
  affiliateId: string,
): void {
  if (userId === null) {
    return;
  }
  try {
    window.sessionStorage.setItem(storageKey(userId), affiliateId);
  } catch {
    // Storage is unavailable (private mode, sandboxed iframe). The switch still
    // applies for this page's lifetime; it just will not survive a reload.
  }
}
